// Pure pagination math for the bloodwork-analysis PDF's "Supplements" panel.
//
// WHY this exists rather than a page-break-inside rule: html2pdf measures page
// breaks against `pageSize.inner.px.height` (floor of the page height converted
// from mm — 1073px for this report) but slices the rendered canvas against
// `floor(canvas.width * inner.ratio)` (1074px). The two grids drift by 1px per
// page, so ~20 pages in, its keep-together pass believes a block sits safely
// inside a page while the slicer cuts straight through it. Every other long
// section in this PDF is pre-paginated in JS for the same reason (see
// pdfMooPagination / the section packer in bloodwork-analysis-pdf.tsx): budget
// each page well under its true capacity, force the break explicitly, and let
// the slack absorb the drift.
//
// Blocks are one pattern each — its heading plus the products under it — so a
// break lands between patterns. A block too tall for a page on its own is split
// between products, and the continuation slice is flagged so the caller can
// mark the repeated heading "(cont.)".
//
// Heights are caller-provided estimates (pixels). The packer is pure — no DOM
// measurement — which keeps it deterministic and testable.

export type SupplementsBlock<TItem> = {
  key: string;
  // Set only on the block that opens a brand ("Systemic Formulas"), so the
  // brand heading travels with its first pattern and can't be left stranded at
  // the foot of a page.
  brandLabel: string | null;
  label: string | null;
  items: TItem[];
  isContinuation: boolean;
};

export type SupplementsPaginationInput<TItem> = {
  blocks: SupplementsBlock<TItem>[];
  // Usable height per page. Deliberately well below the true page height —
  // that margin is what absorbs the grid drift described above.
  pageCapacity: number;
  // Card header + body padding, charged once per page.
  cardOverhead: number;
  brandHeadingHeight: number;
  patternHeadingHeight: number;
  itemHeight: number;
};

export function packSupplementBlocks<TItem>(
  input: SupplementsPaginationInput<TItem>,
): SupplementsBlock<TItem>[][] {
  const {
    blocks,
    pageCapacity,
    cardOverhead,
    brandHeadingHeight,
    patternHeadingHeight,
    itemHeight,
  } = input;

  const pages: SupplementsBlock<TItem>[][] = [];
  let page: SupplementsBlock<TItem>[] = [];
  let used = cardOverhead;

  const startNewPage = () => {
    if (page.length > 0) pages.push(page);
    page = [];
    used = cardOverhead;
  };

  blocks.forEach(block => {
    // The brand heading is charged to the first slice only; continuation
    // slices repeat the pattern heading but never the brand.
    let brandLabel = block.brandLabel;
    let remaining = block.items;
    let isContinuation = block.isContinuation;
    let slice = 0;

    // Splitting is a last resort. A pattern that would fit on a page of its own
    // moves there whole rather than leaving a stray product or two behind —
    // without this, a 9-product pattern arriving with 8 products' worth of room
    // left would strand its last product under a repeated heading on the next
    // page, which reads worse than the whitespace it saves.
    const fullHeight =
      (brandLabel ? brandHeadingHeight : 0) +
      (block.label !== null ? patternHeadingHeight : 0) +
      block.items.length * itemHeight;
    if (
      page.length > 0 &&
      used + fullHeight > pageCapacity &&
      cardOverhead + fullHeight <= pageCapacity
    ) {
      startNewPage();
    }

    for (;;) {
      const fixed =
        (brandLabel ? brandHeadingHeight : 0) +
        (block.label !== null ? patternHeadingHeight : 0);
      const room = pageCapacity - used - fixed;
      const fits = itemHeight > 0 ? Math.floor(room / itemHeight) : remaining.length;

      // Not even the heading plus one product fits — move to a fresh page and
      // re-measure. Guarded on a non-empty page so a block that can never fit
      // (a pattern with more products than a page holds) still lands somewhere
      // instead of looping.
      if (page.length > 0 && fits < 1 && remaining.length > 0) {
        startNewPage();
        continue;
      }

      const take = Math.max(1, Math.min(remaining.length, fits));
      page.push({
        key: slice === 0 ? block.key : `${block.key}-cont${slice}`,
        brandLabel,
        label: block.label,
        items: remaining.slice(0, take),
        isContinuation,
      });
      used += fixed + take * itemHeight;
      remaining = remaining.slice(take);
      if (remaining.length === 0) break;

      // Spilled: the rest of this pattern continues on the next page.
      startNewPage();
      brandLabel = null;
      isContinuation = true;
      slice += 1;
    }
  });

  if (page.length > 0) pages.push(page);
  return pages;
}
